import { Type, type Static } from "@sinclair/typebox";
import { DOCUMENT_PLATFORM_SCHEMA_VERSION } from "./document-platform.ts";
import { UuidSchema } from "./identifiers.ts";

const TimestampSchema = Type.String({ format: "date-time" });
const PathSchema = Type.String({ minLength: 1, maxLength: 1024 });
const NullablePathSchema = Type.Union([PathSchema, Type.Literal("")]);

export const FileProjectionStatusSchema = Type.Union([
  Type.Literal("ready"),
  Type.Literal("pending"),
  Type.Literal("unavailable")
]);
export type FileProjectionStatusV1 = Static<typeof FileProjectionStatusSchema>;

export const DocumentKnowledgeStatusSchema = Type.Union([
  Type.Literal("not_selected"),
  Type.Literal("processing"),
  Type.Literal("knowledge_ready"),
  Type.Literal("graph_ready")
]);
export type DocumentKnowledgeStatusV1 = Static<typeof DocumentKnowledgeStatusSchema>;

export const OpenCloudFileResourceSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    resourceId: Type.String({ minLength: 1, maxLength: 1024 }),
    path: NullablePathSchema,
    name: Type.String({ maxLength: 512 }),
    mediaType: Type.String({ minLength: 1, maxLength: 256 }),
    sizeBytes: Type.Integer({ minimum: 0 }),
    eTag: Type.String({ minLength: 1, maxLength: 256 }),
    isFolder: Type.Boolean(),
    lastModifiedAt: Type.Union([TimestampSchema, Type.Null()]),
    permissions: Type.String({ maxLength: 128 }),
    projectionStatus: FileProjectionStatusSchema,
    documentId: Type.Union([UuidSchema, Type.Null()]),
    knowledgeStatus: Type.Union([DocumentKnowledgeStatusSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type OpenCloudFileResourceV1 = Static<typeof OpenCloudFileResourceSchema>;

export const FileListResponseSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    fileSpaceId: UuidSchema,
    path: NullablePathSchema,
    resources: Type.Array(OpenCloudFileResourceSchema, { maxItems: 100_000 })
  },
  { additionalProperties: false }
);
export type FileListResponseV1 = Static<typeof FileListResponseSchema>;

export const DocumentOverlayBatchInputSchema = Type.Object(
  {
    resourceIds: Type.Array(Type.String({ minLength: 1, maxLength: 1024 }), {
      minItems: 1,
      maxItems: 100,
      uniqueItems: true
    })
  },
  { additionalProperties: false }
);
export type DocumentOverlayBatchInputV1 = Static<typeof DocumentOverlayBatchInputSchema>;

export const DocumentOverlayItemSchema = Type.Object(
  {
    resourceId: Type.String({ minLength: 1, maxLength: 1024 }),
    documentId: Type.Union([UuidSchema, Type.Null()]),
    projectionStatus: Type.Union([Type.Literal("ready"), Type.Literal("pending")]),
    knowledgeStatus: Type.Union([DocumentKnowledgeStatusSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type DocumentOverlayItemV1 = Static<typeof DocumentOverlayItemSchema>;

export const DocumentOverlayBatchResponseSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    fileSpaceId: UuidSchema,
    items: Type.Array(DocumentOverlayItemSchema, { maxItems: 100 })
  },
  { additionalProperties: false }
);
export type DocumentOverlayBatchResponseV1 = Static<typeof DocumentOverlayBatchResponseSchema>;

export const FileMutationResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    resource: Type.Union([OpenCloudFileResourceSchema, Type.Null()]),
    projectionStatus: Type.Literal("pending")
  },
  { additionalProperties: false }
);
export type FileMutationResultV1 = Static<typeof FileMutationResultSchema>;

export const FileCreateFolderInputSchema = Type.Object(
  { path: PathSchema },
  { additionalProperties: false }
);
export type FileCreateFolderInputV1 = Static<typeof FileCreateFolderInputSchema>;

export const FileMoveInputSchema = Type.Object(
  {
    destinationPath: PathSchema,
    ifMatch: Type.String({ minLength: 1, maxLength: 256 })
  },
  { additionalProperties: false }
);
export type FileMoveInputV1 = Static<typeof FileMoveInputSchema>;

export const FileCopyInputSchema = Type.Object(
  {
    destinationFileSpaceId: UuidSchema,
    destinationPath: PathSchema,
    ifMatch: Type.String({ minLength: 1, maxLength: 256 })
  },
  { additionalProperties: false }
);
export type FileCopyInputV1 = Static<typeof FileCopyInputSchema>;

export const FileDeleteInputSchema = Type.Object(
  { ifMatch: Type.String({ minLength: 1, maxLength: 256 }) },
  { additionalProperties: false }
);
export type FileDeleteInputV1 = Static<typeof FileDeleteInputSchema>;

export const FileRestoreInputSchema = Type.Object(
  {
    trashPath: Type.String({ minLength: 1, maxLength: 1024 }),
    destinationPath: PathSchema
  },
  { additionalProperties: false }
);
export type FileRestoreInputV1 = Static<typeof FileRestoreInputSchema>;

export const TusUploadCreateInputSchema = Type.Object(
  {
    parentPath: NullablePathSchema,
    fileName: Type.String({ minLength: 1, maxLength: 512, pattern: "^(?!\\.{1,2}$)[^/\\\\]+$" }),
    sizeBytes: Type.Integer({ minimum: 0 }),
    sha1: Type.Optional(Type.String({ pattern: "^[a-f0-9]{40}$" }))
  },
  { additionalProperties: false }
);
export type TusUploadCreateInputV1 = Static<typeof TusUploadCreateInputSchema>;

export const TusUploadSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    uploadPath: Type.String({ minLength: 1, maxLength: 4096 }),
    uploadToken: Type.String({ minLength: 1, maxLength: 8192 }),
    offset: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type TusUploadV1 = Static<typeof TusUploadSchema>;

export const TusUploadChunkResultSchema = Type.Object(
  {
    offset: Type.Integer({ minimum: 0 }),
    resourceId: Type.Union([Type.String({ minLength: 1, maxLength: 1024 }), Type.Null()]),
    eTag: Type.Union([Type.String({ minLength: 1, maxLength: 256 }), Type.Null()])
  },
  { additionalProperties: false }
);
export type TusUploadChunkResultV1 = Static<typeof TusUploadChunkResultSchema>;

export const OpenCloudTrashResourceSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    resourceId: Type.String({ minLength: 1, maxLength: 1024 }),
    path: PathSchema,
    name: Type.String({ minLength: 1, maxLength: 512 }),
    isFolder: Type.Boolean(),
    deletedAt: Type.Union([TimestampSchema, Type.Null()]),
    originalPath: PathSchema,
    trashPath: Type.String({ minLength: 1, maxLength: 1024 })
  },
  { additionalProperties: false }
);
export type OpenCloudTrashResourceV1 = Static<typeof OpenCloudTrashResourceSchema>;

export const TrashListResponseSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    items: Type.Array(OpenCloudTrashResourceSchema, { maxItems: 100_000 })
  },
  { additionalProperties: false }
);
export type TrashListResponseV1 = Static<typeof TrashListResponseSchema>;

export const OpenCloudFileVersionSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    versionId: Type.String({ minLength: 1, maxLength: 1024 }),
    eTag: Type.String({ minLength: 1, maxLength: 256 }),
    mediaType: Type.String({ minLength: 1, maxLength: 256 }),
    sizeBytes: Type.Integer({ minimum: 0 }),
    lastModifiedAt: Type.Union([TimestampSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type OpenCloudFileVersionV1 = Static<typeof OpenCloudFileVersionSchema>;

export const FileVersionListResponseSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    items: Type.Array(OpenCloudFileVersionSchema, { maxItems: 10_000 })
  },
  { additionalProperties: false }
);
export type FileVersionListResponseV1 = Static<typeof FileVersionListResponseSchema>;

export const OpenCloudReconciliationRunSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    runId: UuidSchema,
    trigger: Type.Union([
      Type.Literal("bootstrap"),
      Type.Literal("retention_gap"),
      Type.Literal("failed_event"),
      Type.Literal("failed_repair"),
      Type.Literal("etag_drift"),
      Type.Literal("manual")
    ]),
    status: Type.Union([
      Type.Literal("queued"),
      Type.Literal("running"),
      Type.Literal("succeeded"),
      Type.Literal("failed")
    ]),
    observedFileCount: Type.Integer({ minimum: 0 }),
    trashedFileCount: Type.Integer({ minimum: 0 }),
    missingDocumentCount: Type.Integer({ minimum: 0 }),
    driftCount: Type.Integer({ minimum: 0 }),
    errorCode: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    queuedAt: TimestampSchema,
    startedAt: Type.Union([TimestampSchema, Type.Null()]),
    completedAt: Type.Union([TimestampSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type OpenCloudReconciliationRunV1 = Static<typeof OpenCloudReconciliationRunSchema>;

export const OpenCloudProjectionHealthSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    fileSpaceId: UuidSchema,
    lastEvent: Type.Union([
      Type.Object(
        {
          eventType: Type.String({ minLength: 1, maxLength: 256 }),
          outcome: Type.Union([Type.Literal("applied"), Type.Literal("skipped")]),
          eventTimestamp: TimestampSchema,
          committedAt: TimestampSchema
        },
        { additionalProperties: false }
      ),
      Type.Null()
    ]),
    checkpoint: Type.Union([
      Type.Object(
        {
          verifiedRootETag: Type.String({ minLength: 1, maxLength: 256 }),
          verifiedStreamSequence: Type.Integer({ minimum: 0 }),
          verifiedAt: TimestampSchema
        },
        { additionalProperties: false }
      ),
      Type.Null()
    ]),
    unresolvedFailureCount: Type.Integer({ minimum: 0 }),
    latestReconciliation: Type.Union([OpenCloudReconciliationRunSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type OpenCloudProjectionHealthV1 = Static<typeof OpenCloudProjectionHealthSchema>;

export const ReconciliationRequestResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    runId: UuidSchema,
    status: Type.Union([Type.Literal("accepted"), Type.Literal("coalesced")])
  },
  { additionalProperties: false }
);
export type ReconciliationRequestResultV1 = Static<typeof ReconciliationRequestResultSchema>;

export const VerifiedFileSpaceSnapshotSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_PLATFORM_SCHEMA_VERSION),
    sourceObservation: Type.Object(
      {
        schemaVersion: Type.Literal("1.0"),
        fileSpaceId: UuidSchema,
        rootETag: Type.String({ minLength: 1, maxLength: 256 }),
        streamSequence: Type.Integer({ minimum: 0 }),
        verifiedAt: TimestampSchema
      },
      { additionalProperties: false }
    ),
    documents: Type.Array(
      Type.Object(
        {
          documentId: UuidSchema,
          openCloudResourceId: Type.String({ minLength: 1, maxLength: 1024 }),
          path: PathSchema,
          name: Type.String({ minLength: 1, maxLength: 512 }),
          mediaType: Type.String({ minLength: 1, maxLength: 256 }),
          sizeBytes: Type.Integer({ minimum: 0 }),
          sourceETag: Type.String({ minLength: 1, maxLength: 256 }),
          sourceChecksum: Type.String({ pattern: "^[a-f0-9]{64}$" }),
          representationResultChecksum: Type.String({ pattern: "^[a-f0-9]{64}$" }),
          modifiedAt: TimestampSchema
        },
        { additionalProperties: false }
      ),
      { maxItems: 100_000 }
    )
  },
  { additionalProperties: false }
);
export type VerifiedFileSpaceSnapshotV1 = Static<typeof VerifiedFileSpaceSnapshotSchema>;

export const OpenCloudObserverErrorDetailsSchema = Type.Object(
  {
    fileSpaceId: Type.Optional(UuidSchema),
    documentIds: Type.Optional(Type.Array(UuidSchema, { maxItems: 100_000, uniqueItems: true })),
    reason: Type.Optional(
      Type.Union([
        Type.Literal("bootstrap"),
        Type.Literal("retention_gap"),
        Type.Literal("failed_event"),
        Type.Literal("failed_repair"),
        Type.Literal("etag_drift")
      ])
    )
  },
  { additionalProperties: false }
);
export type OpenCloudObserverErrorDetailsV1 = Static<typeof OpenCloudObserverErrorDetailsSchema>;

export const OpenCloudObserverErrorResponseSchema = Type.Object(
  {
    error: Type.Object(
      {
        code: Type.String({ minLength: 1, maxLength: 128 }),
        message: Type.String({ minLength: 1, maxLength: 1024 }),
        retryable: Type.Boolean(),
        details: OpenCloudObserverErrorDetailsSchema
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);
export type OpenCloudObserverErrorResponseV1 = Static<typeof OpenCloudObserverErrorResponseSchema>;
